import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import {
	addMessageReply,
	markMessageItemAsRead,
	MessageEntryType,
	MessageItemUpdateType,
	useMessageItem,
	useUpdateMessageItem,
} from '@ukpostbox/client-data';
import { axios } from '@ukpostbox/client-data/lib/AxiosPublic';
import { useNavigate } from 'react-router';
import { SetPageTitle } from '@ukpostbox/frontend-framework/lib/Components/Framework/MetaData';
import { useTranslation } from '@ukpostbox/frontend-framework';
import {
	MessageCentreThreadContextType,
	MessageCentreThreadProviderType,
} from '../Types/MessageCentreThreadProviderTypes';
import { useMessageCentreThreadStore } from './Stores/MessageCentreThreadStore';
import { useMessageCentre } from './MessageCentreProvider';
import { useMessageCentreStore } from './Stores/MessageCentreStore';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';

const MessageCentreThreadContext = React.createContext<MessageCentreThreadContextType>(
	{} as MessageCentreThreadContextType,
);

function MessageCentreThreadProvider(props: PropsWithChildren<MessageCentreThreadProviderType>) {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const { currentLocale } = useSite();
	const { FilterSingleActions, UpdateThreadStatus } = useMessageCentre();

	const maxAttachmentSizeInBytes = 52428800;
	const currentThreadId = useMessageCentreStore((state) => state.currentThreadId);

	const dispatchMessageCentreThreadStoreUpdate = useMessageCentreThreadStore(
		(state) => state.dispatchMessageCentreThreadStoreUpdate,
	);

	const { status, data, error } = useMessageItem(currentThreadId as number);
	const { mutate: updateMessage } = useUpdateMessageItem(currentThreadId as number, 'all');

	const ReplyToThread = () => {
		const threadId = useMessageCentreStore.getState().currentThreadId;

		if (threadId) {
			const messageContent = useMessageCentreThreadStore.getState().messageContent;
			const messageAttachments = useMessageCentreThreadStore.getState().messageAttachments;

			UpdateThreadStatus(true);

			dispatchMessageCentreThreadStoreUpdate({
				type: 'replyingToThread',
			});

			const formData = new FormData();

			formData.append('text', messageContent);
			formData.append('containsHtml', 'false');

			const shapedAttachments: Array<any> = [];

			if (messageAttachments && messageAttachments.length > 0) {
				messageAttachments.forEach((file: any) => {
					formData.append('files', file);
					shapedAttachments.push({ fileName: file.name });
				});
			}

			addMessageReply(threadId, formData)
				.then(() => {
					updateMessage({
						newData: { message: messageContent, attachments: shapedAttachments },
						updateType: MessageItemUpdateType.REPLY,
					});
				})
				.finally(() => {
					UpdateThreadStatus(false, threadId);

					dispatchMessageCentreThreadStoreUpdate({
						type: 'replyToThreadComplete',
					});
				});
		}
	};

	const MarkMessageAsRead = () => {
		const threadId = useMessageCentreStore.getState().currentThreadId;

		if (threadId) {
			UpdateThreadStatus(true);

			markMessageItemAsRead(threadId.toString()).then(() => {
				updateMessage({
					newData: { hasNewReply: false },
					updateType: MessageItemUpdateType.MARK_AS_READ,
				});

				UpdateThreadStatus(false, threadId);
			});
		}
	};

	const MessageAttachments = (e: any) => {
		if (e.target.files) {
			const filesArray = Array.from(e.target.files);
			let totalSize = 0;

			filesArray.forEach((file: any) => {
				totalSize += file.size || 0;
			});

			if (totalSize > maxAttachmentSizeInBytes) {
				dispatchMessageCentreThreadStoreUpdate({
					type: 'updateMessageAttachments',
					attachmentsManagerOpen: false,
					messageAttachments: [],
					attachmentSize: totalSize,
					attachmentSizeError: true,
					attachmentCountError: false,
				});
			} else if (filesArray.length > 10) {
				dispatchMessageCentreThreadStoreUpdate({
					type: 'updateMessageAttachments',
					attachmentsManagerOpen: false,
					messageAttachments: [],
					attachmentSize: totalSize,
					attachmentSizeError: false,
					attachmentCountError: true,
				});
			} else {
				dispatchMessageCentreThreadStoreUpdate({
					type: 'updateMessageAttachments',
					attachmentsManagerOpen: filesArray.length > 0,
					messageAttachments: filesArray,
					attachmentSize: totalSize,
					attachmentSizeError: false,
					attachmentCountError: false,
				});
			}
		}
	};

	const RemoveMessageAttachment = (index: number) => {
		const messageAttachments = useMessageCentreThreadStore.getState().messageAttachments;
		const newAttachments = [...messageAttachments];
		newAttachments.splice(index, 1);

		dispatchMessageCentreThreadStoreUpdate({
			type: 'removeMessageAttachment',
			messageAttachments: newAttachments,
		});
	};

	const UpdateMessageValue = (value: string) => {
		dispatchMessageCentreThreadStoreUpdate({
			type: 'updateMessageValue',
			content: value,
		});
	};

	const UpdateComposeContainer = (state: boolean) => {
		dispatchMessageCentreThreadStoreUpdate({
			type: 'toggleComposeContainer',
			state,
		});
	};

	const GroupData = (thisData: Array<MessageEntryType>) => {
		const groups: Record<string, Array<MessageEntryType>> = thisData.reduce((thisGroup: any, item) => {
			const date = item.dateSent.toString().split('T')[0];

			if (!thisGroup[date]) {
				// eslint-disable-next-line no-param-reassign
				thisGroup[date] = [];
			}

			thisGroup[date].push(item);

			return thisGroup;
		}, {});

		dispatchMessageCentreThreadStoreUpdate({
			type: 'groupMessageData',
			data: groups,
		});
	};

	useEffect(() => {
		SetPageTitle({ title: `${data?.department?.name} - ${t('messageCentre.title')}` });
	}, [currentLocale, data]);

	useEffect(() => {
		if (axios.isAxiosError(error) && error.status === 404) {
			navigate('/message-centre', { replace: true });
		}
	}, [error]);

	useEffect(() => {
		if (data && data.messages) {
			GroupData(data.messages);
			FilterSingleActions(data, [data.id]);
		}
		if (data && data.hasNewReply) {
			MarkMessageAsRead();
		}
	}, [data]);

	useEffect(() => {
		document.addEventListener('change', MessageAttachments);
		return () => document.removeEventListener('change', MessageAttachments);
	}, []);

	const messageCentreThreadMemo = useMemo(
		() => ({
			data,
			status,
			error,

			maxAttachmentSizeInBytes,

			ReplyToThread,
			MarkMessageAsRead,

			MessageAttachments,
			RemoveMessageAttachment,

			UpdateMessageValue,
			UpdateComposeContainer,
		}),
		[data, status, error],
	);

	return (
		<MessageCentreThreadContext.Provider value={messageCentreThreadMemo}>
			{props.children}
		</MessageCentreThreadContext.Provider>
	);
}

const useMessageCentreThread = () => useContext(MessageCentreThreadContext);

export { MessageCentreThreadContext, useMessageCentreThread };

export default MessageCentreThreadProvider;
